/**
 * 阿里云 QuickTracking 标准版 埋点工具
 * 
 * 官方文档：
 * - 引入&配置：https://help.aliyun.com/zh/document_detail/280490.html
 * - 基础功能：https://help.aliyun.com/zh/quick-tracking/latest/basic-function
 * - 埋点API：https://help.aliyun.com/zh/document_detail/2850968.html
 * 
 * 产品说明：
 * - QuickTracking 标准版使用 aplus_queue 队列机制
 * - 通过 CDN Script 方式引入 SDK
 * - 支持自动采集 PV、性能、错误等
 * - 支持自定义事件埋点
 */

// ==================== 类型定义 ====================

/** 页面配置对象 */
export interface QTPageConfig {
  /** 页面名称 */
  page_name?: string
  /** 页面标题 */
  page_title?: string
  /** 是否自动 PV（默认 true） */
  is_auto?: boolean
  /** 其他自定义页面属性 */
  [key: string]: any
}

/** 用户/页面自定义属性 */
export interface QTUserData {
  /** 用户 ID */
  user_id?: string
  /** 其他自定义用户属性 */
  [key: string]: any
}

/** 事件参数对象 */
export interface QTEventParams {
  /** 其他自定义事件属性 */
  [key: string]: any
}

// ==================== 核心工具函数 ====================

/**
 * 向 aplus_queue 推送指令
 * 
 * @param action 动作名称（如 'aplus.setMetaInfo'）
 * @param args 参数数组
 */
function pushToQueue(action: string, args: any[]): void {
  if (typeof window === 'undefined') return
  
  window.aplus_queue = window.aplus_queue || []
  window.aplus_queue.push({
    action,
    arguments: args
  })
}

// ==================== 页面浏览 API ====================

/**
 * 发送页面浏览日志（PV）
 * 
 * @param pageConfig 页面配置对象
 * @param userData 用户/页面自定义属性
 * 
 * 使用示例：
 * ```typescript
 * sendPageView(
 *   { page_name: 'course_detail', page_title: '课程详情' },
 *   { course_id: '12345', course_type: 'live' }
 * )
 * ```
 * 
 * 注意：SDK 默认自动采集 PV，此函数用于添加自定义属性
 */
export function sendPageView(
  pageConfig: QTPageConfig = { is_auto: false },
  userData: QTUserData = {}
): void {
  try {
    pushToQueue('aplus.sendPV', [
      pageConfig,
      {
        timestamp: Date.now(),
        url: window.location.href,
        ...userData
      }
    ])
    
    if (import.meta.env.DEV) {
      console.log('[QuickTracking] PV 上报:', { pageConfig, userData })
    }
  } catch (error) {
    console.error('[QuickTracking] PV 上报失败:', error)
  }
}

// ==================== 自定义事件 API ====================

/**
 * 发送自定义事件
 * 
 * @param eventName 事件名称（建议使用 模块_动作_对象 格式）
 * @param eventType 事件类型（常用：'CLK' 点击, 'EXP' 曝光, 'CUST' 自定义）
 * @param params 事件参数
 * 
 * 使用示例：
 * ```typescript
 * // 点击事件
 * recordEvent('btn_click', 'CLK', { btn_id: 'submit', from: 'home' })
 * 
 * // 自定义事件
 * recordEvent('video_play', 'CUST', { video_id: '123', duration: 300 })
 * ```
 */
export function recordEvent(
  eventName: string,
  eventType: string = 'CUST',
  params: QTEventParams = {}
): void {
  try {
    pushToQueue('aplus.record', [
      eventName,
      eventType,
      {
        timestamp: Date.now(),
        url: window.location.href,
        ...params
      }
    ])
    
    if (import.meta.env.DEV) {
      console.log(`[QuickTracking] 事件上报: ${eventName}`, { eventType, params })
    }
  } catch (error) {
    console.error('[QuickTracking] 事件上报失败:', error)
  }
}

/**
 * 记录点击事件（便捷方法）
 * 
 * @param eventName 事件名称
 * @param params 事件参数
 * 
 * 使用示例：
 * ```typescript
 * trackClick('btn_click', { btn_id: 'submit' })
 * ```
 */
export function trackClick(eventName: string, params: QTEventParams = {}): void {
  recordEvent(eventName, 'CLK', params)
}

/**
 * 记录曝光事件（便捷方法）
 * 
 * @param eventName 事件名称
 * @param params 事件参数
 * 
 * 使用示例：
 * ```typescript
 * trackExposure('banner_exposure', { banner_id: '123' })
 * ```
 */
export function trackExposure(eventName: string, params: QTEventParams = {}): void {
  recordEvent(eventName, 'EXP', params)
}

/**
 * 记录自定义事件（通用方法）
 * 
 * @param eventName 事件名称
 * @param params 事件参数
 * 
 * 使用示例：
 * ```typescript
 * trackCustomEvent('form_submit', { form_name: 'registration', success: true })
 * ```
 */
export function trackCustomEvent(eventName: string, params: QTEventParams = {}): void {
  recordEvent(eventName, 'CUST', params)
}

// ==================== 用户属性 API ====================

/**
 * 设置用户 ID
 * 
 * @param userId 用户账号 ID
 * 
 * 使用示例：
 * ```typescript
 * // 登录成功后调用
 * setUserId('user_12345')
 * ```
 */
export function setUserId(userId: string): void {
  try {
    pushToQueue('aplus.setMetaInfo', ['_user_id', userId])
    
    if (import.meta.env.DEV) {
      console.log('[QuickTracking] 用户 ID 已设置:', userId)
    }
  } catch (error) {
    console.error('[QuickTracking] 设置用户 ID 失败:', error)
  }
}

/**
 * 清除用户信息（登出时调用）
 * 
 * 使用示例：
 * ```typescript
 * // 登出时调用
 * clearUserInfo()
 * ```
 */
export function clearUserInfo(): void {
  try {
    pushToQueue('aplus.setMetaInfo', ['_user_id', ''])
    
    if (import.meta.env.DEV) {
      console.log('[QuickTracking] 用户信息已清除')
    }
  } catch (error) {
    console.error('[QuickTracking] 清除用户信息失败:', error)
  }
}

// ==================== 辅助函数 ====================

/**
 * 监听 SDK 初始化完成
 * 
 * @param callback 回调函数
 * 
 * 使用示例：
 * ```typescript
 * onReady(() => {
 *   console.log('SDK 已就绪')
 *   // 可以安全调用 API
 * })
 * ```
 */
export function onReady(callback: () => void): void {
  if (typeof window === 'undefined') return
  
  window.aplus_queue = window.aplus_queue || []
  window.aplus_queue.push({
    action: 'aplus.aplus_pubsub.subscribe',
    arguments: ['aplusReady', function(status: string) {
      if (status === 'complete') {
        console.log('[QuickTracking] SDK 初始化完成')
        callback()
      }
    }]
  })
}

/**
 * 暂停日志发送
 * 
 * 使用场景：业务数据未准备好时暂停上报
 * 注意：必须与 resumeTracking() 成对使用
 */
export function pauseTracking(): void {
  pushToQueue('aplus.setMetaInfo', ['_hold', 'BLOCK'])
  console.log('[QuickTracking] 已暂停日志发送')
}

/**
 * 恢复日志发送
 * 
 * 注意：必须与 pauseTracking() 成对使用
 */
export function resumeTracking(): void {
  pushToQueue('aplus.setMetaInfo', ['_hold', 'START'])
  console.log('[QuickTracking] 已恢复日志发送')
}

// ==================== 全局类型声明 ====================

declare global {
  interface Window {
    aplus_queue: any[]
    aplus?: {
      getMetaInfo: (metaName: string) => any
    }
  }
}

// ==================== 默认导出 ====================

export default {
  // 页面浏览
  sendPageView,
  
  // 自定义事件
  recordEvent,
  trackClick,
  trackExposure,
  trackCustomEvent,
  
  // 用户属性
  setUserId,
  clearUserInfo,
  
  // 辅助
  onReady,
  pauseTracking,
  resumeTracking
}
